import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { isConsoleRole } from '@/constants/common';
import { legacyTarget } from '@/lib/legacy-routes';
import { Shell } from '@/components/console/Shell';
import LoginPage from '@/pages/LoginPage';
import GoogleAuthSuccessPage from '@/pages/GoogleAuthSuccessPage';
import TodayPage from '@/pages/console/TodayPage';
import PeoplePage from '@/pages/console/PeoplePage';
import MessagesPage from '@/pages/console/MessagesPage';
import ProductPage from '@/pages/console/ProductPage';
import SettingsPage from '@/pages/console/SettingsPage';
import SystemPage from '@/pages/console/SystemPage';
import PersonPage from '@/pages/console/PersonPage';
import NeedsPage from '@/pages/console/NeedsPage';
import GrowthPage from '@/pages/console/GrowthPage';
import SessionPage from '@/pages/console/SessionPage';
import PersonSessionsPage from '@/pages/console/PersonSessionsPage';
import FailedSessionsPage from '@/pages/console/FailedSessionsPage';
import MonitoringPage from '@/pages/console/MonitoringPage';
import HelpPage from '@/pages/console/HelpPage';
import FoundingPage from '@/pages/console/FoundingPage';
import BusinessPage from '@/pages/console/BusinessPage';
import ReliabilityPage from '@/pages/console/ReliabilityPage';

/**
 * admin.posture.vision: the console pages behind one gate. SUPER_ADMIN only; a
 * persisted session with any other role is signed out on the spot.
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, admin, logout } = useAuthStore();
  const location = useLocation();
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  if (!isConsoleRole(admin?.role)) {
    logout();
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

/** Old links (the consumer app's /admin?tab=..&user=.., this app's old paths) land on the right page. */
const LegacyRedirect = () => {
  const location = useLocation();
  return <Navigate to={legacyTarget(location.pathname, location.search)} replace />;
};

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/google-auth-success" element={<GoogleAuthSuccessPage />} />

      <Route path="/" element={<ProtectedRoute><Shell /></ProtectedRoute>}>
        <Route index element={<TodayPage />} />
        <Route path="people" element={<PeoplePage />} />
        <Route path="people/:id" element={<PersonPage />} />
        <Route path="people/:id/sessions" element={<PersonSessionsPage />} />
        <Route path="people/:id/sessions/:peerId" element={<SessionPage />} />
        <Route path="sessions/failed" element={<FailedSessionsPage />} />
        <Route path="needs" element={<NeedsPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="product" element={<ProductPage />} />
        <Route path="system" element={<SystemPage />} />
        <Route path="monitoring" element={<MonitoringPage />} />
        <Route path="founding" element={<FoundingPage />} />
        <Route path="growth" element={<GrowthPage />} />
        <Route path="business" element={<BusinessPage />} />
        <Route path="reliability" element={<ReliabilityPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route path="/admin" element={<LegacyRedirect />} />
      <Route path="/dashboard/*" element={<LegacyRedirect />} />
      <Route path="/users/*" element={<LegacyRedirect />} />
      <Route path="/tickets/*" element={<LegacyRedirect />} />
      <Route path="/admins" element={<LegacyRedirect />} />
      <Route path="/audit-log" element={<LegacyRedirect />} />
      <Route path="/system-health" element={<LegacyRedirect />} />
      <Route path="/providers/*" element={<LegacyRedirect />} />
      <Route path="/provider-applications/*" element={<LegacyRedirect />} />
      <Route path="/exercise-requests" element={<LegacyRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
